import { Component, computed, effect, inject, signal, input, LOCALE_ID } from '@angular/core';
import { CommonModule, formatDate } from '@angular/common';
import { RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';

import { EChartsOption } from 'echarts';
import { NgxEchartsDirective, provideEcharts } from 'ngx-echarts';

import { RegisterComponent } from '../../components/component-lookup';
import { VisualBaseComponent } from '../visual-base.component';

import { forkJoin } from 'rxjs';

import { DateRange, StateService } from '../../services/state.service';
import { RateService } from '../../services/rate.service';
import { VisualService } from '../../services/visual.service';
import { RegressionDropdown, RegressionRate } from '../../services/drop.down.service';
import { ChartInfoBoxComponent } from "../chart-info-box/chart-info-box.component";
import { DateService } from '../../services/date.service';
import { SystemSettingService } from '../../services/system.setting.service';



@RegisterComponent('NmdLineChart')
@Component({
  selector: 'app-regression',
  standalone: true,
  imports: [CommonModule, RouterModule, FormsModule, NgxEchartsDirective, ChartInfoBoxComponent],
  templateUrl: './nmd-line-chart.component.html',
  styleUrl: './nmd-line-chart.component.scss',
  providers: [provideEcharts()]
})
export class NmdLineChartComponent extends VisualBaseComponent {
  readonly institution = inject(StateService).institution;
  readonly dateRange = inject(StateService).dateRange;
  private rateService = inject(RateService);
  private dateService = inject(DateService);
  private systemSettingService = inject(SystemSettingService);
  private fixedDateRange: DateRange = { startDate: new Date("2000-01-01"), endDate: new Date("2000-01-01") };
  private locale = inject(LOCALE_ID);

  ngOnInit(): void {

    this.systemSettingService.getSystemSettingValue("StartNmdReportDate")
      .subscribe(value => {
        this.fixedDateRange.startDate = new Date(value);
      });

    this.systemSettingService.getSystemSettingValue("CurrentNmdReportDate")
      .subscribe(value => {
        this.fixedDateRange.endDate = new Date(value);
      });

  }

  override initOptions = { height: 275 }
  override baseOptions: EChartsOption = {
    grid: {
      left: 10,
      right: 25,
      top: 20,
      bottom: 40,
      containLabel: true
    },
    legend: {
      selectedMode: true
    },
    xAxis: {
      type: 'value',
      show: true
    },
    yAxis: {
      type: 'value',
      show: true,
      splitLine: {
        lineStyle: {
          color: '#ccc'
        }
      }
    },
    series: [
      {
        type: 'line',
        symbol: 'none'
      },
      {
        type: 'line',
        symbol: 'none'
      }
    ]
  };
  override tooltipOptions: EChartsOption = {
    tooltip: {
      trigger: 'axis',
      valueFormatter: value => VisualService.getValueDisplay(Number(value), "PCT2")
    }
  };
  override printOptions: EChartsOption = {
    animation: false,
    series: [
      {
        type: 'line'
      },
      {
        type: 'line',
        symbol: 'none',
        symbolSize: 7,
        lineStyle: {
          type: [20, 5],
          dashOffset: 5,
        }
      },
      {
        type: 'line',
        symbol: 'none'
      }
    ]
  };

  readonly correlationMetrics = signal<RegressionDropdown[]>([]);
  readonly metric = signal<RegressionDropdown | undefined>(undefined);
  readonly correlationRates = computed(() => this.metric()?.rates ?? []);
  readonly rate = signal<RegressionRate | undefined>(undefined);
  noData = false;

  constructor() {
    super();
    effect(() => {
      const dateRange = this.dateRange();
      const rate = this.rateService.selectedRegressionRate();

      if (this.chartInit() && rate && dateRange) {
        this.updateVisual(this.fixedDateRange, rate, dateRange);
      }
    })
  }

  public updateVisual(dateRange: DateRange, rate: RegressionRate, sliderDateRange: DateRange) {
    forkJoin({
      rateData: this.rateService.getRateData(rate.rateIdStatistical, 'StartNmdReportDate')
    }).subscribe(({ rateData }) => {
      const dateSeries = this.dateService.getDateSeries(this.institution().institutionType, dateRange, undefined, 'non-maturity-deposits');

      const valueMap = rateData.reduce((acc: { [x: string]: any; }, entry: { reportDate: string; value: any; }) => {
        const dateKey = entry.reportDate.substring(0, 10);
        acc[dateKey] = entry.value;
        return acc;
      }, {} as Record<string, number>);

      const yAxisValues = dateSeries.dates.map(date =>
        valueMap[date.toISOString().substring(0, 10)] ?? null
      );

      if (yAxisValues.filter(v => v != null).length == 0) {
        this.echartsInstance?.clear();
        this.chartOptions = this.noDataOptions;
        this.noData = true;
      }
      else {
        const startIdx = dateSeries.labels.indexOf(formatDate(sliderDateRange.startDate, 'MMM yyyy', this.locale));
        const endIdx = dateSeries.labels.indexOf(formatDate(sliderDateRange.endDate, 'MMM yyyy', this.locale));

        const fullLength = dateSeries.labels.length;
        const segment1 = new Array(fullLength).fill(null);
        const segment2 = new Array(fullLength).fill(null);
        // Fill segment1 with values outside the range — including edges
        for (let i = 0; i < startIdx; i++) {
          segment1[i] = yAxisValues[i];
        }
        segment1[startIdx] = yAxisValues[startIdx]; // include start point

        for (let i = endIdx + 1; i < fullLength; i++) {
          segment1[i] = yAxisValues[i];
        }
        segment1[endIdx] = yAxisValues[endIdx]; // include end point

        // Fill segment2 with values inside the range
        for (let i = startIdx; i <= endIdx; i++) {
          segment2[i] = yAxisValues[i];
        }

        this.updateOptions = {

          tooltip: {
            trigger: 'axis',
            formatter: function (params: any) {
              const items = Array.isArray(params) ? params : [params];

              let content = params[0]?.axisValueLabel ? params[0].axisValueLabel + '<br/>' : '';

              items.forEach(item => {
                if (item.data != null) {
                  content += `${item.marker}${item.seriesName}: ${item.data}<br/>`;
                }
              });

              // Return empty string instead of null to satisfy TypeScript
              return content || '';
            }
          },

          xAxis: {
            data: dateSeries.labels,
            type: 'category'
          },
          yAxis: [
            {
              axisLabel: {
                formatter: function (value: number) {
                  return VisualService.getValueDisplay(value, 'PCT2');
                }
              }
            }
          ],
          series: [
            {
              name: `${rate.rateName}`,
              data: segment1,
              type: 'line',
              lineStyle: {
                type: "dotted"
              },
              color: "#5F5F5F",
              connectNulls: false
            },
            {
              name: `${rate.rateName}`,
              data: segment2,
              type: 'line',
              color: "#2b821d",
              connectNulls: false
            }
          ]
        };
      }
      this.echartsInstance?.hideLoading();
    });
  }
}
